// apps/admin/src/routes/organizations/OrganizationsList.tsx
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Plus, Search, Building2, MoreHorizontal, ShieldAlert, Eye, Pencil, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { useDateFormat } from "../../hooks/useDateFormat";
import { SuperAdminGuard } from "../../components/SuperAdminGuard";
import {
  Input,
  Button,
  Badge,
  Skeleton,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  useToast,
} from "@jourfx/ui";

// Convex ID type helper
type Id<T extends string> = string & { __tableName: T };
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge, type StatusType } from "../../components/StatusBadge";
import { EmptyState } from "../../components/EmptyState";

// Organization type from Convex query
interface Organization {
  _id: string;
  name: string;
  slug: string;
  status: string;
  userCount: number;
  license?: {
    tier: string;
    validUntil: number;
  };
}

function OrganizationsListContent() {
  const { t } = useTranslation('organizations');
  const { t: tc } = useTranslation('common');
  const { formatShortDate } = useDateFormat();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [orgToArchive, setOrgToArchive] = useState<Organization | null>(null);

  const archiveOrg = useMutation(api.admin.organizations.archive);

  const handleEditClick = (orgId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Navigate to detail page for editing
    navigate({ to: "/organizations/$orgId", params: { orgId } });
  };

  const handleArchiveClick = (org: Organization, e: React.MouseEvent) => {
    e.stopPropagation();
    setOrgToArchive(org);
    setArchiveDialogOpen(true);
  };

  const handleConfirmArchive = async () => {
    if (!orgToArchive) return;
    try {
      await archiveOrg({ id: orgToArchive._id as Id<"organizations"> });
      toast({
        title: t('archiveSuccess.title'),
        description: t('archiveSuccess.description', { name: orgToArchive.name }),
      });
    } catch (error) {
      toast({
        title: tc('error.title'),
        description: error instanceof Error ? error.message : tc('error.unknown'),
        variant: "destructive",
      });
    } finally {
      setArchiveDialogOpen(false);
      setOrgToArchive(null);
    }
  };

  const orgsData = useQuery(api.admin.organizations.list, {
    status: statusFilter !== "all" ? statusFilter as "active" | "trial" | "suspended" | "archived" : undefined,
  });

  if (orgsData === undefined) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-56" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-10 w-44" />
        </div>
        <div className="flex flex-col gap-4 sm:flex-row">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="rounded-lg border bg-card overflow-hidden">
          <div className="divide-y divide-border">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-36" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="h-6 w-24" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-8 w-8" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (orgsData === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <ShieldAlert className="h-12 w-12 text-warning" />
        <h2 className="text-xl font-semibold text-foreground">{tc('auth.loginRequired')}</h2>
        <p className="text-muted-foreground text-center max-w-md">
          {tc('auth.loginRequiredMessage')}
        </p>
        <Link
          to="/login"
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {tc('auth.toLogin')}
        </Link>
      </div>
    );
  }

  const organizations: Organization[] = (orgsData?.organizations as Organization[]) || [];

  const filteredOrgs = organizations.filter((org: Organization) => {
    const matchesSearch =
      org.name.toLowerCase().includes(search.toLowerCase()) ||
      org.slug.toLowerCase().includes(search.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t('title')}
        description={t('subtitle')}
        actions={
          <Button asChild>
            <Link to="/organizations/new">
              <Plus className="mr-2 h-4 w-4" />
              {t('addOrganization')}
            </Link>
          </Button>
        }
      />

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <option value="all">{tc('table.allStatuses')}</option>
          <option value="active">{tc('status.active')}</option>
          <option value="trial">{tc('status.trial')}</option>
          <option value="suspended">{tc('status.suspended')}</option>
          <option value="archived">{tc('status.archived')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.organization')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.license')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('table.users')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {tc('status.active').split(' ')[0]}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {tc('table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredOrgs.map((org: Organization) => (
              <tr
                key={org._id}
                className="group transition-colors hover:bg-muted/50 cursor-pointer"
                onClick={() => navigate({ to: "/organizations/$orgId", params: { orgId: org._id } })}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                      <Building2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{org.name}</div>
                      <div className="text-sm text-muted-foreground">{org.slug}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {org.license ? (
                    <div className="space-y-1">
                      <Badge
                        variant={
                          org.license.tier === "enterprise"
                            ? "default"
                            : org.license.tier === "professional"
                            ? "secondary"
                            : "outline"
                        }
                        className={
                          org.license.tier === "enterprise"
                            ? "bg-primary/10 text-primary hover:bg-primary/10"
                            : org.license.tier === "professional"
                            ? "bg-info-muted text-info-muted-foreground hover:bg-info-muted"
                            : ""
                        }
                      >
                        {org.license.tier}
                      </Badge>
                      <div className="text-xs text-muted-foreground">
                        {t('table.validUntil', { date: formatShortDate(org.license.validUntil) })}
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-muted-foreground">{t('table.noLicense')}</span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className="inline-flex items-center gap-1.5 tabular-nums">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-foreground">{org.userCount}</span>
                  </span>
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={org.status as StatusType} />
                </td>
                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Menü öffnen</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem asChild>
                        <Link to="/organizations/$orgId" params={{ orgId: org._id }}>
                          <Eye className="mr-2 h-4 w-4" />
                          {tc('actions.view')}
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={(e) => handleEditClick(org._id, e)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {tc('actions.edit')}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => handleArchiveClick(org, e)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {tc('actions.archive')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredOrgs.length === 0 && (
        <EmptyState
          icon={Building2}
          title={t('empty.title')}
          description={t('empty.message')}
          action={{
            label: t('addOrganization'),
            onClick: () => navigate({ to: "/organizations/new" }),
            icon: Plus,
          }}
        />
      )}

      {/* Archive Confirmation Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('archiveDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('archiveDialog.description', { name: orgToArchive?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tc('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmArchive}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {tc('actions.archive')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function OrganizationsList() {
  return (
    <SuperAdminGuard>
      <OrganizationsListContent />
    </SuperAdminGuard>
  );
}
